// Button — design system primitive.
// Four variants × two sizes. Never apply box-shadow to a button.
// See Harness DS v1.0 §07.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Optional leading icon (lucide-react). */
  icon?: ReactNode;
  /** Optional trailing icon. */
  trailing?: ReactNode;
}

const BASE =
  "inline-flex items-center gap-1.5 font-medium tracking-[0.005em] " +
  "border transition-colors duration-120 ease-harness " +
  "active:translate-y-[0.5px] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const SIZES: Record<Size, string> = {
  sm: "h-7 px-2.5 text-[12px] rounded-lg",
  md: "h-8 px-3.5 text-[12.5px] rounded-lg",
};

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-[var(--accent)] text-white border-transparent hover:bg-[oklch(0.58_0.18_var(--accent-h))]",
  secondary: "bg-surface text-ink border-border hover:border-border-strong",
  ghost: "bg-transparent text-ink border-transparent hover:bg-ink/[0.04]",
  danger: "bg-[var(--danger)] text-white border-transparent hover:opacity-90",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "sm",
    icon,
    trailing,
    className = "",
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={`${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${className}`}
      {...rest}
    >
      {icon}
      {children}
      {trailing}
    </button>
  );
});

export default Button;

/**
 * IconButton — square button for icon-only affordances. 28h compact / 32h
 * for forms. Same focus-ring + variants as Button.
 */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Required for screen readers. */
  "aria-label": string;
}

const ICON_SIZES: Record<Size, string> = {
  sm: "h-7 w-7 rounded-lg",
  md: "h-8 w-8 rounded-lg",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      variant = "ghost",
      size = "sm",
      className = "",
      type = "button",
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={`inline-flex items-center justify-center border transition-colors duration-120 ease-harness focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring disabled:opacity-50 disabled:cursor-not-allowed ${ICON_SIZES[size]} ${VARIANTS[variant]} ${className}`}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
